import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  EditModal, DetailPanel, ConfirmModal,
  exportSingleInvoice, deleteInvoices, postInvoiceToErp,
} from "../components/invoiceShared";
import toast from "react-hot-toast";

const PAGE_SIZE = 25; 

export default function InvoiceHistory({ rows, onDeleted, onUpdated, onBack }) {
  const { hasPermission } = useAuth();
  const [search,    setSearch]    = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [editRow,   setEditRow]   = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); 
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [postingIds, setPostingIds] = useState(new Set());

  const postToErp = async (row) => {
    if (postingIds.has(row.id)) return;
    setPostingIds(prev => new Set(prev).add(row.id));
    try {
      const res = await postInvoiceToErp(row.id);
      if (res.posted_to_erp) toast.success("Posted to ERP successfully");
      else if (res.already_in_erp) toast.error("Already sent to ERP earlier");
      else toast.error(res.error || "ERP push failed");
      onUpdated && onUpdated({ id: row.id, erp_posted: res.erp_posted });
    } catch (e) {
      toast.error(e.response?.data?.error || "ERP push failed");
    } finally {
      setPostingIds(prev => { const n = new Set(prev); n.delete(row.id); return n; });
    }
  };

  const canEdit   = hasPermission("edit_invoice");
  const canDelete = hasPermission("delete_invoice");
  const canExport = hasPermission("export_csv");

  const sentinelRef = useRef(null);
  const scrollRef    = useRef(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const headerMatch = Object.entries(r).some(([k, v]) =>
        k !== "items" && String(v ?? "").toLowerCase().includes(q)
      );
      const itemMatch = r.items?.some(item =>
        Object.values(item).some(v => String(v ?? "").toLowerCase().includes(q))
      );
      return headerMatch || itemMatch;
    });
  }, [rows, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")));
  }, [filtered]);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search]);

  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < sorted.length;

  const loadMore = useCallback(() => {
    setVisibleCount(c => Math.min(c + PAGE_SIZE, sorted.length));
  }, [sorted.length]);

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { root: scrollRef.current, rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const deleteSingle = id => setConfirmDelete({ ids: [id] });

  const runDelete = async () => {
    if (!confirmDelete) return;
    const { ids } = confirmDelete;
    try {
      await deleteInvoices(ids);
      toast.success(ids.length > 1 ? `${ids.length} record(s) deleted` : "Record deleted");
      onDeleted(ids);
    } catch {
      toast.error("Delete failed");
    } finally {
      setConfirmDelete(null);
    }
  };

  const f = v => (v != null && v !== "" ? String(v) : null);

  return (
    <div className="history-page">
      <div className="history-toolbar">
        <button className="btn-ghost history-back" onClick={onBack}>← Back to Invoices</button>
        <input
          className="grid-search history-search"
          placeholder="🔍 Search across all fields & items..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
        <span className="grid-count">
          {sorted.length} record{sorted.length !== 1 ? "s" : ""}
          {search && ` (filtered from ${rows.length})`}
        </span>
      </div>

      <div className="history-split">
      <div className="history-table-wrap" ref={scrollRef}>
        {sorted.length === 0 ? (
          <div className="empty-state">
            <span style={{ fontSize: 40 }}>📄</span>
            <p>{search ? "No invoices match your search." : "No invoices yet."}</p>
          </div>
        ) : (
          <>
            <table className="data-table history-table">
              <thead>
                <tr>
                  <th>Invoice No.</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Uploaded By</th>
                  <th>Uploaded On</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(row => (
                  <tr key={row.id}>
                    <td style={{ fontFamily: "var(--mono)" }}>{f(row.invoice_no) || <span className="null-cell">—</span>}</td>
                    <td>{f(row.seller_name) || <span className="null-cell">—</span>}</td>
                    <td style={{ fontWeight: 600, color: "var(--accent)" }}>
                      {f(row.total_inr_figures) ? `₹ ${row.total_inr_figures}` : <span className="null-cell">—</span>}
                    </td>
                    <td>{f(row.uploaded_by) || <span className="null-cell">—</span>}</td>
                    <td style={{ fontSize: 12, color: "var(--text2)" }}>
                      {f(row.uploaded_time_ist)?.slice(0, 16).replace("T", " ") || <span className="null-cell">—</span>}
                    </td>
                    <td>
                      <div className="history-row-actions">
                        <button className="btn-tiny" onClick={() => setDetailRow(row)} title="View details"> 👁</button>
                        {canEdit && (
                          <button
                            className="btn-tiny"
                            style={{ color: "var(--accent2)", borderColor: "rgba(92,78,232,.3)" }}
                            onClick={() => setEditRow(row)}
                            title="Edit"
                          >✏</button>
                        )}
                        {canExport && (
                          <button
                            className="btn-tiny btn-tiny-export"
                            onClick={() => exportSingleInvoice(row)}
                            title="Export CSV"
                          >⬇</button>
                        )}
                        {row.erp_posted ? (
                          <span className="btn-tiny" style={{ color: "var(--ok, #a6e3a1)", borderColor: "rgba(166,227,161,.35)", cursor: "default" }} title="Already posted to ERP">✅</span>
                        ) : (row.vendor_verify_status !== "mismatch_pending" && row.vendor_verify_status !== "mismatch_rejected") && (
                          <button
                            className="btn-tiny"
                            style={{ color: "var(--ok, #a6e3a1)", borderColor: "rgba(166,227,161,.35)" }}
                            onClick={() => postToErp(row)}
                            disabled={postingIds.has(row.id)}
                            title="Post to ERP"
                          >{postingIds.has(row.id) ? "⏳" : "📤"}</button>
                        )}
                        {canDelete && (
                          <button
                            className="btn-tiny"
                            style={{ color: "var(--err)", borderColor: "rgba(220,38,38,.3)" }}
                            onClick={() => deleteSingle(row.id)}
                            title="Delete"
                          >🗑</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {hasMore && (
              <div ref={sentinelRef} className="history-load-sentinel">
                <div className="spinner" style={{ width: 22, height: 22, borderWidth: 2 }} />
                <span>Loading more…</span>
              </div>
            )}
            {!hasMore && sorted.length > PAGE_SIZE && (
              <div className="history-end-marker">— End of results —</div>
            )}
          </>
        )}
      </div>

      {detailRow && <DetailPanel row={detailRow} onClose={() => setDetailRow(null)} />}
      </div>

      {editRow && (
        <EditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={updated => { onUpdated && onUpdated(updated); setEditRow(null); }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete record"
          message="Are you sure you want to delete this record? This cannot be undone."
          onConfirm={runDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}